import { cn } from '@liza/ui/utils/cn';
import { useCarePlanRowExpansion } from './CarePlanRowExpansionContext';

/**
 * Wraps a care-plan row's secondary lines (instructions, dose, schedule). When `compact`, they're
 * hidden by default and unfurl either on card hover/focus (the legacy board + patient-web) or via the
 * board's "show details" toggle when a host opts into controlled expansion — see
 * {@link CarePlanRowExpansionContext}. Rendered plainly (always visible) otherwise, e.g. the detail
 * overlay and the add-to-plan menu. Either way the lines stay in the DOM, so screen readers reach
 * them; the collapse is purely a CSS height/opacity transition.
 */

/** The lines themselves. `pt-1` above them matches the row grid's spacing, so title → instructions →
 *  schedule are evenly spaced. */
const LINES = 'flex flex-col gap-1 pt-1';

export function CarePlanItemSecondary({
  compact,
  children,
}: {
  compact?: boolean;
  children: React.ReactNode;
}): React.ReactNode {
  const { controlled, expanded } = useCarePlanRowExpansion();
  if (!compact) return <div className={LINES}>{children}</div>;
  // Controlled hosts drive the unfurl entirely from their "show details" toggle — no hover.
  if (controlled) {
    return (
      <div
        data-testid="care-plan-item-secondary"
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200',
          expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {/* The spacing is on a descendant, not on the clipped box itself: a grid item's own padding is
              a floor its track cannot go under, so carrying it here left every collapsed row 4px tall. */}
          <div className={LINES}>{children}</div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="care-plan-item-secondary"
      className={cn(
        'grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-200',
        'group-hover/card:grid-rows-[1fr] group-hover/card:opacity-100 group-hover/card:delay-200',
        // Keyboard parity: focusing anything inside the card reveals the content too (no hover needed),
        // immediately so tabbing through the rows isn't gated behind a delay.
        'group-focus-within/card:grid-rows-[1fr] group-focus-within/card:opacity-100',
        // Also stay open while the card is pinned expanded — e.g. when editing an item within it, so
        // the content shows behind the edit overlay.
        'group-data-[expanded=true]/card:grid-rows-[1fr] group-data-[expanded=true]/card:opacity-100',
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className={LINES}>{children}</div>
      </div>
    </div>
  );
}
